import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { Supplier } from 'api/supplier/suppliers.types'
import { taxisAPI } from 'api/vehicle/taxisAPI'
import { Taxi } from 'api/vehicle/taxisAPI.types'
import { FC, useEffect, useState } from 'react'

interface VehicleListProps {
  supplier: Supplier
  onBack: () => void
  onProceed: () => void
}

const VehicleList: FC<VehicleListProps> = ({ supplier, onBack, onProceed }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [vehicles, setVehicles] = useState<Taxi[]>([])
  const [fetchError, setFetchError] = useState<string>()

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await taxisAPI.getTaxisBySupplierId(supplier.id)
        setVehicles(response.data.value)
      } catch {
        setFetchError('Failed to retrieve vehicles.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchVehicles()
  }, [supplier.id])

  const hasVehicles = vehicles.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Checking for vehicles...</Typography>
        </Box>
      )}

      {fetchError && <Typography color="error">{fetchError}</Typography>}

      {!isLoading && !fetchError && hasVehicles && (
        <>
          <Typography variant="subtitle1">
            {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} found
          </Typography>
          <List dense disablePadding>
            {vehicles.map((v) => (
              <ListItem key={v.id} disableGutters>
                <ListItemText
                  primary={v.vehicleTypeName}
                  secondary={
                    <>
                      {'Supplier Ref: '}
                      {v.supplierReference == null ||
                      (v.supplierReference as unknown as string) === '' ? (
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          unmapped
                        </Box>
                      ) : (
                        v.supplierReference
                      )}
                      {` | Capacity: ${v.capacity} | Fleet: ${v.fleetSize}`}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {!isLoading && !fetchError && !hasVehicles && (
        <Alert severity="info" icon={<InfoOutlinedIcon />}>
          No vehicles have been set up for this supplier yet. Please add
          vehicles in the Vehicles tab before continuing with the integration
          setup. You can return to this step once vehicles are configured.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        {hasVehicles && (
          <Button variant="contained" onClick={onProceed}>
            Continue to Vehicle Mapping
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default VehicleList
